import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { SEC_ApiCall, SEC_DataApiPost } from '../func.js';
import { userAppData } from '../MainApp.js';
import LoadingComp from '../common/loading.js';
import { BookingQuestionsModal } from '../common/modals.js';
import './Alert.css';

const GENERIC_ERROR_MESSAGE = 'We could not load this alert right now. Please try again shortly.';

/**
 * Displays a single alert with its key metadata and a styled preview matching
 * the rest of the Alert module.
 */
export default function AlertSingleView() {
    const { alertId } = useParams();
    const navigate = useNavigate();

    const [alertData, setAlertData] = useState(null);
    const [adminMessage, setAdminMessage] = useState('');
    const [adminSending, setAdminSending] = useState(false);
    const [endAlertMessage, setEndAlertMessage] = useState('');
    const [endAlertConfirmed, setEndAlertConfirmed] = useState(false);
    const [endAlertModalOpen, setEndAlertModalOpen] = useState(false);
    const [endAlertSending, setEndAlertSending] = useState(false);
    const [editTextModalOpen, setEditTextModalOpen] = useState(false);
    const [editTextTitle, setEditTextTitle] = useState('');
    const [editTextSubtitle, setEditTextSubtitle] = useState('');
    const [editTextSending, setEditTextSending] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);
    const refreshInProgress = useRef(false);
    const isMountedRef = useRef(true);

    const hasAccess = Number(userAppData?.alertData?.perms ?? 0) === 1;
    const toastConfig = useMemo(() => ({ position: 'top-right', autoClose: 4000 }), []);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const fetchAlert = useCallback(async (options = {}) => {
        const { background = false, force = false } = options;

        if (!alertId || !hasAccess) {
            if (isMountedRef.current && !background) {
                setIsLoading(false);
                setIsLoaded(true);
            }
            return;
        }

        if (refreshInProgress.current && !force) {
            return;
        }

        refreshInProgress.current = true;

        if (!background && isMountedRef.current) {
            setIsLoading(true);
            setIsLoaded(false);
            setHasError(false);
        }

        try {
            const response = await SEC_ApiCall('alert/single', { alertId });

            if (response?.message === 'Unauthorized' || (response?.statusCode && response.statusCode >= 400)) {
                if (isMountedRef.current) {
                    setHasError(true);
                    if (!background) {
                        setIsLoading(false);
                        setIsLoaded(true);
                    }
                }
                return;
            }

            const parsedResponse = response?.body ? safeParse(response.body) : response;

            if (parsedResponse?.message === 'Unauthorized' || (parsedResponse?.statusCode && parsedResponse.statusCode >= 400)) {
                if (isMountedRef.current) {
                    setHasError(true);
                    if (!background) {
                        setIsLoading(false);
                        setIsLoaded(true);
                    }
                }
                return;
            }

            if (isMountedRef.current) {
                setAlertData(parsedResponse);
                setHasError(false);
                if (!background) {
                    setIsLoading(false);
                    setIsLoaded(true);
                }
            }
        } catch (error) {
            console.log('Single alert fetch failed', error);
            if (isMountedRef.current && (!background || !alertData)) {
                setHasError(true);
                if (!background) {
                    setIsLoading(false);
                    setIsLoaded(true);
                }
            }
        } finally {
            refreshInProgress.current = false;
            if (isMountedRef.current) {
                setIsLoaded(true);
                if (!background) {
                    setIsLoading(false);
                }
            }
        }
    }, [alertId, hasAccess]);

    useEffect(() => {
        if (!alertId || !hasAccess) {
            setIsLoading(false);
            return;
        }

        setIsLoaded(false);
        setHasError(false);
        fetchAlert();
    }, [alertId, hasAccess, fetchAlert]);

    useEffect(() => {
        if (!alertData?.active) {
            return undefined;
        }

        const intervalId = setInterval(() => {
            fetchAlert({ background: true });
        }, 7000);

        return () => clearInterval(intervalId);
    }, [alertData?.active, fetchAlert]);

    const displayConfig = useMemo(() => alertData?.displayConfig || {}, [alertData]);
    const previewColours = useMemo(() => ({
        backgroundColor: displayConfig.bgColour || '#f9b233',
        color: displayConfig.textColour || '#ffffff',
        borderColor: displayConfig.textColour || '#ffffff'
    }), [displayConfig]);
    const previewTextStyle = useMemo(() => ({
        color: previewColours.color
    }), [previewColours]);
    const previewChipStyle = useMemo(() => ({
        color: previewColours.color,
        borderColor: previewColours.borderColor,
        backgroundColor: 'rgba(255, 255, 255, 0.18)'
    }), [previewColours]);
    const previewToneChipStyle = useMemo(() => ({
        color: previewColours.color,
        borderColor: previewColours.borderColor,
        backgroundColor: 'rgba(255, 255, 255, 0.25)'
    }), [previewColours]);
    const handleOpenAlertWindow = useCallback(() => {
        if (!alertData?.alertUrl) {
            return;
        }

        try {
            window.open(alertData.alertUrl, '_blank', 'noopener');
        } catch (error) {
            console.log('Failed to open alert window', error);
        }
    }, [alertData]);

    const createdAtDisplay = formatDate(alertData?.createdAt);
    const validUntilDisplay = formatDate(alertData?.validUntil);
    const rawMessages = useMemo(
        () => (Array.isArray(alertData?.messages) ? alertData.messages : []),
        [alertData]
    );
    const adminMessages = useMemo(() => {
        if (Array.isArray(alertData?.adminMessages)) {
            return alertData.adminMessages;
        }
        return rawMessages.filter(isAdminMessage);
    }, [alertData, rawMessages]);
    const clientMessages = useMemo(() => {
        if (Array.isArray(alertData?.adminMessages)) {
            return rawMessages;
        }
        return rawMessages.filter((message) => !isAdminMessage(message));
    }, [alertData, rawMessages]);
    const isAlertActive = Boolean(alertData?.active);
    const canSendAdminMessage = adminMessage.trim().length > 0 && !adminSending && isAlertActive;
    const canSubmitEndAlert = Boolean(
        isAlertActive && endAlertMessage.trim().length > 0 && endAlertConfirmed && !endAlertSending
    );

    const isNonSuccessResponse = useCallback((rawResponse, parsed) => {
        if (!rawResponse && !parsed) {
            return true;
        }

        const statusCode = Number(rawResponse?.statusCode ?? parsed?.statusCode ?? 0);
        if (statusCode && statusCode !== 200) {
            return true;
        }

        return Boolean(
            rawResponse?.message === 'Unauthorized' ||
            parsed?.message === 'Unauthorized' ||
            rawResponse?.message === 'Internal server error' ||
            parsed?.message === 'Internal server error' ||
            rawResponse?.errorType ||
            parsed?.errorType ||
            rawResponse?.data === 'error' ||
            parsed?.data === 'error'
        );
    }, []);

    const handleOpenEditTextModal = useCallback(() => {
        const titleValue = (alertData?.displayConfig?.title || alertData?.title || '').trim();
        const subtitleValue = (alertData?.displayConfig?.subtitle || alertData?.subtitle || '').trim();
        setEditTextTitle(titleValue);
        setEditTextSubtitle(subtitleValue);
        setEditTextModalOpen(true);
    }, [alertData]);

    const handleCloseEditTextModal = useCallback(() => {
        setEditTextModalOpen(false);
        setEditTextTitle('');
        setEditTextSubtitle('');
        setEditTextSending(false);
    }, []);

    // Validate and submit admin messages, then refresh the alert timeline.
    const handleSendAdminMessage = useCallback(async () => {
        const trimmedMessage = adminMessage.trim();

        if (!alertId) {
            toast.error('❌ Missing alert reference. Please refresh and try again.', toastConfig);
            return;
        }

        if (!trimmedMessage) {
            toast.error('❌ Please enter a message before sending.', toastConfig);
            return;
        }

        if (!isAlertActive) {
            toast.error('❌ This alert is inactive. Admin messages are disabled.', toastConfig);
            return;
        }

        if (adminSending) {
            return;
        }

        setAdminSending(true);

        try {
            const payload = {
                alertId,
                message: trimmedMessage
            };
            const response = await SEC_DataApiPost('alert/messages/admin', JSON.stringify(payload));
            const parsedResponse = response?.body ? safeParse(response.body) : response;

            if (isNonSuccessResponse(response, parsedResponse)) {
                toast.error('❌ Unable to send admin message. Please try again.', toastConfig);
                return;
            }

            toast.success('✅ Admin message sent', toastConfig);
            setAdminMessage('');
            fetchAlert({ background: true });
        } catch (error) {
            console.log('Admin message send failed', error);
            toast.error('❌ Failed to send admin message. Please try again.', toastConfig);
        } finally {
            setAdminSending(false);
        }
    }, [adminMessage, adminSending, alertId, fetchAlert, isNonSuccessResponse, toastConfig]);

    const handleOpenEndAlertModal = useCallback(() => {
        setEndAlertMessage('');
        setEndAlertConfirmed(false);
        setEndAlertModalOpen(true);
    }, []);

    const handleCloseEndAlertModal = useCallback(() => {
        setEndAlertModalOpen(false);
        setEndAlertMessage('');
        setEndAlertConfirmed(false);
        setEndAlertSending(false);
    }, []);

    const handleUpdateAlertText = useCallback(async () => {
        const trimmedTitle = editTextTitle.trim();
        const trimmedSubtitle = editTextSubtitle.trim();

        if (!alertId) {
            toast.error('❌ Missing alert reference. Please refresh and try again.', toastConfig);
            return;
        }

        if (!trimmedTitle) {
            toast.error('❌ Please enter a title.', toastConfig);
            return;
        }

        if (!trimmedSubtitle) {
            toast.error('❌ Please enter a subtitle.', toastConfig);
            return;
        }

        if (editTextSending) {
            return;
        }

        setEditTextSending(true);

        try {
            const payload = {
                PK: userAppData?.schoolCode || '',
                SK: alertId,
                alertId,
                title: trimmedTitle,
                subtitle: trimmedSubtitle
            };
            const response = await SEC_DataApiPost('alert/text', JSON.stringify(payload));
            const parsedResponse = response?.body ? safeParse(response.body) : response;

            if (isNonSuccessResponse(response, parsedResponse)) {
                toast.error('❌ Unable to update alert text. Please try again.', toastConfig);
                return;
            }

            toast.success('✅ Alert text updated', toastConfig);
            handleCloseEditTextModal();
            fetchAlert({ background: false, force: true });
        } catch (error) {
            console.log('Alert text update failed', error);
            toast.error('❌ Failed to update alert text. Please try again.', toastConfig);
        } finally {
            setEditTextSending(false);
        }
    }, [
        alertId,
        editTextSending,
        editTextSubtitle,
        editTextTitle,
        fetchAlert,
        handleCloseEditTextModal,
        isNonSuccessResponse,
        toastConfig
    ]);

    const handleEndAlertSubmit = useCallback(async () => {
        const trimmedMessage = endAlertMessage.trim();

        if (!alertId) {
            toast.error('❌ Missing alert reference. Please refresh and try again.', toastConfig);
            return;
        }

        if (!trimmedMessage) {
            toast.error('❌ Please enter an alert end message.', toastConfig);
            return;
        }

        if (!endAlertConfirmed) {
            toast.error('❌ Please confirm the alert end message.', toastConfig);
            return;
        }

        if (!isAlertActive) {
            toast.error('❌ This alert is already inactive.', toastConfig);
            return;
        }

        if (endAlertSending) {
            return;
        }

        setEndAlertSending(true);

        try {
            const payload = {
                PK: userAppData?.schoolCode || '',
                SK: alertId,
                alertId,
                inactiveText: trimmedMessage
            };
            const response = await SEC_DataApiPost('alert/end', JSON.stringify(payload));
            const parsedResponse = response?.body ? safeParse(response.body) : response;

            if (isNonSuccessResponse(response, parsedResponse)) {
                toast.error('❌ Unable to end this alert. Please try again.', toastConfig);
                return;
            }

            toast.success('✅ Alert ended successfully', toastConfig);
            handleCloseEndAlertModal();
            fetchAlert({ background: false, force: true });
        } catch (error) {
            console.log('End alert failed', error);
            toast.error('❌ Failed to end the alert. Please try again.', toastConfig);
        } finally {
            setEndAlertSending(false);
        }
    }, [
        alertId,
        endAlertConfirmed,
        endAlertMessage,
        endAlertSending,
        fetchAlert,
        handleCloseEndAlertModal,
        isNonSuccessResponse,
        isAlertActive,
        toastConfig
    ]);

    if (!hasAccess) {
        return (
            <div className="alert-single-container">
                <div className="alert-single-card alert-single-card--muted">
                    <h2 className="alert-single-heading">Alert Centre - Single Alert</h2>
                    <p className="alert-single-copy">You do not have permission to view alerts.</p>
                    <button type="button" className="alert-single-back" onClick={() => navigate('/app/dashboard')}>
                        Return to dashboard
                    </button>
                </div>
            </div>
        );
    }

    if (isLoading && !isLoaded) {
        return (
            <div className="alert-single-container alert-single-loading">
                <LoadingComp />
            </div>
        );
    }

    if (hasError || !alertData) {
        return (
            <div className="alert-single-container">
                <div className="alert-single-card alert-single-card--muted">
                    <p className="alert-single-copy">{GENERIC_ERROR_MESSAGE}</p>
                    <div className="alert-single-actions">
                        <button
                            type="button"
                            className="alert-single-back"
                            onClick={() => navigate('/app/alert')}
                        >
                            Back to alerts
                        </button>
                        <button
                            type="button"
                            className="alert-create-button"
                            onClick={() => window.location.reload()}
                        >
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="alert-single-container">
            <div className="alert-single-header">
                <div>
                    <h1 className="alert-single-heading">Alert Centre - Single Alert</h1>
                </div>
                <button
                    type="button"
                    className="alert-single-back"
                    onClick={() => navigate('/app/alert')}
                >
                    Back to alerts
                </button>
            </div>

            <div className="alert-single-card">
                <div className="alert-single-layout">
                    <div className="alert-single-main">
                        <div className="alert-single-preview" style={previewColours}>
                            <p className="alert-active-kicker" style={previewTextStyle}>Alert preview</p>
                            <h2 className="alert-single-title" style={previewTextStyle}>
                                {displayConfig.title || 'Active alert'}
                            </h2>
                            {displayConfig.subtitle && (
                                <p className="alert-single-body" style={previewTextStyle}>{displayConfig.subtitle}</p>
                            )}
                            <div className="alert-active-meta">
                                <span className="alert-chip" style={previewChipStyle}>
                                    {alertData.active ? 'Active' : 'Inactive'}
                                </span>
                                {alertData.windowEnforced && (
                                    <span className="alert-chip alert-chip--tone" style={previewToneChipStyle}>
                                        Window enforced
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="alert-single-meta">
                            <div className="alert-single-meta-row">
                                <span className="alert-single-label">Created</span>
                                <span className="alert-single-value">{createdAtDisplay}</span>
                            </div>
                            <div className="alert-single-meta-row">
                                <span className="alert-single-label">Valid until</span>
                                <span className="alert-single-value">{validUntilDisplay}</span>
                            </div>
                            <div className="alert-single-meta-row">
                                <span className="alert-single-label">Created by</span>
                                <span className="alert-single-value">{alertData.creatingUser || 'Unknown'}</span>
                            </div>
                            <div className="alert-single-meta-row">
                                <span className="alert-single-label">Inactive text</span>
                                <span className="alert-single-value">{alertData.inactiveText || 'Not provided'}</span>
                            </div>
                            <div className="alert-single-meta-row">
                                <span className="alert-single-label">Alert Links</span>
                                {alertData.alertUrl ? (
                                    <div className="alert-link-buttons">
                                        <button
                                            type="button"
                                            className="alert-link-button"
                                            onClick={handleOpenAlertWindow}
                                        >
                                            Open Alert Window
                                        </button>
                                        <button type="button" className="alert-link-button alert-link-button--secondary">
                                            Preview Alert
                                        </button>
                                    </div>
                                ) : (
                                    <span className="alert-single-value">Not available</span>
                                )}
                            </div>
                            <div className="alert-single-meta-row alert-management-row">
                                <span className="alert-single-label">Alert Management</span>
                                <div className="alert-management-content">
                                    <div className="alert-management-actions">
                                        <button
                                            type="button"
                                            className="alert-management-button"
                                            onClick={handleOpenEndAlertModal}
                                            disabled={!isAlertActive}
                                        >
                                            End Alert
                                        </button>
                                        <button
                                            type="button"
                                            className="alert-management-button"
                                            onClick={handleOpenEditTextModal}
                                            disabled={!isAlertActive}
                                        >
                                            Update Alert Text
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <aside className="alert-messages-panel" aria-label="Alert messages">
                        <div className="alert-messages-header">
                            <div>
                                <p className="alert-messages-kicker">Client Messages</p>
                            </div>
                        </div>
                        <div className="alert-messages-list">
                            {clientMessages.length === 0 && (
                                <p className="alert-single-copy alert-messages-empty">
                                    No messages have been received for this alert yet.
                                </p>
                            )}
                            {clientMessages.map((message) => (
                                <div className="alert-message-card" key={message.messageId || message.createdAt}>
                                    <div className="alert-message-meta">
                                        <span className="alert-message-user">{message.user || 'Unknown user'}</span>
                                        <span className="alert-message-time">{formatDate(message.createdAt)}</span>
                                    </div>
                                    <p className="alert-message-body">{message.message || 'No message body'}</p>
                                    <div className="alert-message-footer">
                                        <span className="alert-message-device">
                                            {message.deviceName ? `From ${message.deviceName}` : 'Device not recorded'}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </aside>

                    <aside className="alert-messages-panel alert-messages-panel--admin" aria-label="Admin messages">
                        <div className="alert-messages-header">
                            <div>
                                <p className="alert-messages-kicker">Admin Messages</p>
                            </div>
                        </div>
                        <div className="alert-messages-list">
                            {adminMessages.length === 0 && (
                                <p className="alert-single-copy alert-messages-empty">
                                    No admin messages have been sent yet.
                                </p>
                            )}
                            {adminMessages.map((message) => (
                                <div className="alert-message-card" key={message.messageId || message.createdAt}>
                                    <div className="alert-message-meta">
                                        <span className="alert-message-user">{message.user || 'Admin user'}</span>
                                        <span className="alert-message-time">{formatDate(message.createdAt)}</span>
                                    </div>
                                    <p className="alert-message-body">{message.message || 'No message body'}</p>
                                    <div className="alert-message-footer" />
                                </div>
                            ))}
                        </div>
                        <div className="alert-admin-composer">
                            <label className="alert-single-label" htmlFor="alert-admin-message">
                                Send a message
                            </label>
                            <textarea
                                id="alert-admin-message"
                                className="alert-admin-input"
                                placeholder={
                                    isAlertActive
                                        ? 'Type a message for other admins...'
                                        : 'Messages can only be sent during active alerts'
                                }
                                value={adminMessage}
                                onChange={(event) => setAdminMessage(event.target.value)}
                                rows={3}
                                disabled={adminSending || !isAlertActive}
                            />
                            <button
                                type="button"
                                className="alert-admin-send"
                                onClick={handleSendAdminMessage}
                                disabled={!canSendAdminMessage}
                            >
                                {adminSending ? 'Sending...' : 'Send message'}
                            </button>
                        </div>
                    </aside>
                </div>
            </div>

            <BookingQuestionsModal
                modalActiveState={endAlertModalOpen}
                setStateFunction={setEndAlertModalOpen}
                onModalClose={handleCloseEndAlertModal}
            >
                <div className="alert-end-modal">
                    <label className="alert-end-modal-label" htmlFor="alert-end-message">
                        screen alert end message
                    </label>
                    <input
                        type="text"
                        className="alert-end-input"
                        value={endAlertMessage}
                        onChange={(event) => setEndAlertMessage(event.target.value)}
                        placeholder="Enter the alert end message..."
                        disabled={endAlertSending}
                        id="alert-end-message"
                    />
                    <label className="alert-end-checkbox">
                        <input
                            type="checkbox"
                            checked={endAlertConfirmed}
                            onChange={(event) => setEndAlertConfirmed(event.target.checked)}
                            disabled={endAlertSending}
                        />
                        Ending the alert will display the below message above
                    </label>
                    <button
                        type="button"
                        className="alert-end-confirm"
                        onClick={handleEndAlertSubmit}
                        disabled={!canSubmitEndAlert}
                    >
                        {endAlertSending ? 'Ending alert...' : 'Confirm Alert End'}
                    </button>
                </div>
            </BookingQuestionsModal>

            <BookingQuestionsModal
                modalActiveState={editTextModalOpen}
                setStateFunction={setEditTextModalOpen}
                onModalClose={handleCloseEditTextModal}
            >
                <div className="alert-edit-text-modal">
                    <label className="alert-end-modal-label" htmlFor="alert-edit-title">
                        alert title
                    </label>
                    <input
                        type="text"
                        id="alert-edit-title"
                        className="alert-end-input"
                        value={editTextTitle}
                        onChange={(event) => setEditTextTitle(event.target.value)}
                        placeholder="Enter the alert title..."
                        disabled={editTextSending}
                    />
                    <label className="alert-end-modal-label" htmlFor="alert-edit-subtitle">
                        alert subtitle
                    </label>
                    <input
                        type="text"
                        id="alert-edit-subtitle"
                        className="alert-end-input"
                        value={editTextSubtitle}
                        onChange={(event) => setEditTextSubtitle(event.target.value)}
                        placeholder="Enter the alert subtitle..."
                        disabled={editTextSending}
                    />
                    <button
                        type="button"
                        className="alert-end-confirm"
                        onClick={handleUpdateAlertText}
                        disabled={editTextSending}
                    >
                        {editTextSending ? 'Updating text...' : 'Update Alert Text'}
                    </button>
                </div>
            </BookingQuestionsModal>
        </div>
    );
}

function safeParse(value) {
    try {
        return JSON.parse(value);
    } catch (error) {
        return value;
    }
}

function formatDate(rawValue) {
    if (!rawValue) {
        return 'Not set';
    }

    const parsed = new Date(rawValue);
    if (Number.isNaN(parsed.getTime())) {
        return rawValue;
    }

    return new Intl.DateTimeFormat('en-GB', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    }).format(parsed);
}

// Identify admin-only messages for UI filtering.
function isAdminMessage(message) {
    const messageType = String(message?.messageType || '').toUpperCase();
    return messageType === 'ADMIN';
}
